import { MutableRefObject, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";

const InputContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
`;

const IconContainer = styled.div`
  position: absolute;
  left: 11px;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  z-index: 1;
  pointer-events: none;
  color: ${(props) => props.theme.mutedTextColor};
`;

const inputBase = css`
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 8px;
  height: 38px;
  background-color: ${(props) => props.theme.inputBg};
  padding-left: 12px;
  padding-right: 12px;
  outline: 0 !important;
  color: ${(props) => props.theme.textColor};
  font-size: 14px;
  font-family: inherit;
  font-weight: 400;
  -webkit-font-smoothing: antialiased;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: ${(props) => props.theme.mutedTextColor};
  }

  &:hover {
    border-color: ${(props) => props.theme.borderStrong};
  }
`;

const InputDisabled = styled.input<{ minWidth?: number }>`
  ${inputBase}
  min-width: ${(props) =>
    props.minWidth !== undefined ? props.minWidth + "px" : ""};

  ${(props) =>
    props.onClick !== undefined
      ? css`
          &:hover {
            cursor: pointer;
          }
        `
      : ""}
`;

const Input = styled.input<{ $hasIcon?: boolean }>`
  ${inputBase}
  position: relative;
  width: 100%;
  ${(props) => (props.$hasIcon ? "padding-left: 36px;" : "")}

  &:focus {
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.primarySoft};
  }
`;

const TextInput: React.FC<{
  className?: string;
  placeHolder?: string | null;
  disabled?: boolean;
  onClick?: (event: any) => void;
  onSubmit?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onChange?: (value: string) => void;
  focus?: boolean;
  allowEmpty?: boolean;
  minWidth?: number;
  icon?: JSX.Element;
}> = ({
  placeHolder,
  disabled,
  onClick,
  className,
  onSubmit,
  onFocus,
  onBlur,
  focus,
  onChange,
  allowEmpty,
  minWidth,
  icon,
}) => {
  const [value, setValue] = useState("");
  const textInput: MutableRefObject<any> = useRef(null);

  useEffect(() => {
    if (focus) {
      textInput?.current?.focus?.();
    }
  }, [focus]);

  const handleChange = (event: any) => {
    setValue(event.target.value);
    if (onChange) {
      onChange(event.target.value);
    }
  };
  const handleKeyDown = (event: any) => {
    if (event.key === "Enter") {
      console.log("enter pressed " + value);
      if ((value || allowEmpty) && onSubmit) {
        onSubmit(value);
      }
    }
  };

  if (disabled) {
    return (
      <InputDisabled
        ref={textInput}
        minWidth={minWidth}
        value={value}
        className={className}
        placeholder={placeHolder ?? ""}
        readOnly={true}
        //disabled={disabled}
        onFocus={() => {
          textInput.current.blur();
        }}
        onClick={(event) => {
          console.log("onClick");
          if (onClick !== undefined) {
            onClick(event);
          }
        }}
      />
    );
  }

  const handleBlur = () => {
    if (onBlur) {
      onBlur();
    }
  };
  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
  };

  return (
    <InputContainer>
      {icon && (
        <IconContainer className="text-input-icon">{icon}</IconContainer>
      )}
      <Input
        ref={textInput}
        onBlur={handleBlur}
        onFocus={handleFocus}
        value={value}
        className={className}
        placeholder={placeHolder ?? ""}
        disabled={disabled}
        $hasIcon={!!icon}
        onChange={handleChange}
        onClick={(event) => {
          if (onClick !== undefined) {
            onClick(event);
          }
        }}
        onKeyDown={handleKeyDown}
      />
    </InputContainer>
  );
};

export default TextInput;
